/** @fileoverview Lists the files in this chat with on/off switches and a budget warning. */

import { Files, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { omittedCount, pageLabel } from '../../lib/attachments';
import type { ConversationFile, ConversationFilesResponse } from '../../types/attachments';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Switch } from '../ui/switch';
import FileTypeIcon from './FileTypeIcon';

const INDEX_LABEL = {
  indexed: 'Indexed for search',
  indexing: 'Indexing',
  failed: 'Indexing failed',
  none: 'Not indexed',
} as const;

/** Whether the model sees the file: read in full, searchable through its index, over budget, or off. */
const contextStatus = (file: ConversationFile) => {
  if (!file.enabled || file.placement === 'off') return 'Off';
  if (file.placement === 'whole') return 'Reads in full';
  if (file.placement === 'omitted') return 'Over budget';
  return INDEX_LABEL[file.indexStatus];
};

/** The index note beside a status that does not already say it. A file read in full only notes indexing. */
const indexNote = (file: ConversationFile) => {
  if (file.placement === 'whole')
    return file.indexStatus === 'indexing' ? INDEX_LABEL.indexing : undefined;
  if (file.placement === 'omitted' && file.indexStatus !== 'none')
    return INDEX_LABEL[file.indexStatus];
  return undefined;
};

/** Muted line under the name: status, page count, then the indexing note. */
const fileDetails = (file: ConversationFile) =>
  [contextStatus(file), pageLabel(file.name, file.pages), indexNote(file)]
    .filter(Boolean)
    .join(' · ');

export default function ChatFilesPopover({
  data,
  onToggle,
  onDelete,
}: {
  data?: ConversationFilesResponse;
  onToggle: (attachmentId: string, enabled: boolean) => void;
  /** Resolves true when the file was deleted. */
  onDelete: (attachmentId: string) => Promise<boolean>;
}) {
  const [pending, setPending] = useState<ConversationFile>();
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    if (!pending) return;
    setDeleting(true);
    const deleted = await onDelete(pending.id);
    setDeleting(false);
    if (deleted) setPending(undefined);
  };
  const files = data?.attachments ?? [];
  return (
    <>
      {data && files.length > 0 && (
        <Popover>
          <PopoverTrigger
            className="flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-app-text-muted hover:bg-app-surface-muted-strong hover:text-app-text"
            type="button"
            aria-label="Files in this chat"
            title="Files in this chat"
          >
            <Files className="h-4 w-4" aria-hidden="true" />
            <span className="@max-5xl:sr-only">Files</span>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 overflow-x-hidden p-3 text-xs">
            <p className="px-1 pb-2 font-medium text-app-text">Files in this chat</p>
            <ul className="flex max-h-60 flex-col gap-1 overflow-x-hidden overflow-y-auto">
              {files.map((file) => (
                <li className="flex items-center gap-3 rounded-md px-1 py-1.5" key={file.id}>
                  {file.type === 'image' ? (
                    <ImageIcon
                      className="h-4 w-4 shrink-0 text-app-text-muted"
                      aria-hidden="true"
                    />
                  ) : (
                    <FileTypeIcon
                      name={file.name}
                      className="h-4 w-4 shrink-0 text-app-text-muted"
                    />
                  )}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-app-text" title={file.name}>
                      {file.name}
                    </span>
                    <span className="break-words text-app-text-muted">{fileDetails(file)}</span>
                  </span>
                  <Switch
                    className="shrink-0 data-checked:bg-app-brand"
                    aria-label={`Use ${file.name}`}
                    checked={file.enabled}
                    onCheckedChange={(checked) => onToggle(file.id, checked)}
                  />
                  <button
                    aria-label={`Delete ${file.name}`}
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-app-text-muted hover:bg-app-surface-muted-strong hover:text-app-text"
                    onClick={() => setPending(file)}
                    title="Delete file"
                    type="button"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            {omittedCount(data) > 0 && (
              <p className="px-1 pt-2 text-app-warning">
                Some files did not fit the budget. The assistant will search them instead of reading
                them in full.
              </p>
            )}
          </PopoverContent>
        </Popover>
      )}
      <Dialog
        open={Boolean(pending)}
        onOpenChange={(open) => !open && !deleting && setPending(undefined)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this file?</DialogTitle>
            <DialogDescription>
              {pending?.name} is removed from this chat for good, including its search index. This
              can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={deleting}
              onClick={() => setPending(undefined)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={deleting} onClick={confirmDelete} type="button" variant="destructive">
              {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
